/**
 * <ReleaseTour> — Signal DS-themed `react-joyride` (v3) renderer.
 *
 * Pure presentation: all behavior (eligibility, persistence, Mixpanel) lives in
 * `useReleaseTour`. This component only owns the visual theme, so DS token changes touch one
 * file. Joyride handles responsive placement (via `floating-ui`) and keyboard/ARIA behavior
 * itself — no extra work needed here for the "mobile responsive" / "accessible" requirements
 * beyond keeping the popover copy short enough to fit a phone viewport.
 */
import { Joyride, type EventData, type Step } from 'react-joyride';

export interface ReleaseTourProps {
  run: boolean;
  steps: Step[];
  onEvent: (data: EventData) => void;
}

export function ReleaseTour({ run, steps, onEvent }: ReleaseTourProps) {
  return (
    <Joyride
      run={run}
      steps={steps}
      onEvent={onEvent}
      continuous
      options={{
        showProgress: true,
        buttons: ['back', 'close', 'primary', 'skip'],
        primaryColor: 'var(--color-brand-primary)',
        textColor: 'var(--color-text-primary)',
        backgroundColor: 'var(--color-surface-card)',
        arrowColor: 'var(--color-base-white)',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10000,
      }}
      styles={{
        tooltip: {
          borderRadius: 12,
          fontFamily: 'inherit',
          padding: 24,
        },
        tooltipTitle: {
          fontSize: 15,
          fontWeight: 600,
        },
        tooltipContent: {
          fontSize: 13,
          color: 'var(--color-text-muted)',
          padding: '8px 0',
        },
        buttonPrimary: {
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          padding: '8px 16px',
        },
        buttonBack: {
          fontSize: 13,
          color: 'var(--color-text-muted)',
        },
        buttonSkip: {
          fontSize: 13,
          color: 'var(--color-text-muted)',
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        nextWithProgress: 'Next ({current} of {total})',
        skip: 'Skip',
      }}
    />
  );
}
